import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Col, Form, Input, InputNumber, message, Modal,
  Row, Select, Space, Steps, Switch, Tabs, Tag
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { supplierApi, supplierGroupApi } from './supplierApi';
import {
  SUPPLIER_STATUS_OPTIONS,
  SUPPLIER_TYPE_OPTIONS,
  GST_REGISTRATION_TYPE_OPTIONS,
  REGISTRATION_TYPES_REQUIRING_GSTIN,
  GSTIN_RE, PAN_RE, MOBILE_RE, PINCODE_RE, IFSC_RE, UDYAM_RE
} from './supplierConstants';
import './SupplierModal.css';

// ─────────────────────────────────────────────────────────────────
// Glass-i — Supplier Add / Edit / View popup (Stage 6B.3).
//
// Production-grade centered modal (matches Customer popup chrome).
// 3 steps for Add (KYC → GST & Accounting → Bank & Defaults); 5 tabs
// for Edit/View (KYC, GST & Accounting, Bank Details, Linked Ledger,
// Notes / Defaults). View mode shows everything read-only.
//
// Frontend ONLY enforces field shape — every save still goes through
// the backend validator (supplier.validation.js) which is the source
// of truth (state code allow-list, MSME ↔ Udyam, GSTIN ↔ PAN, etc.).
// ─────────────────────────────────────────────────────────────────

const FIELD_LABELS = {
  supplierName:        'Supplier Name',
  supplierType:        'Supplier Type',
  status:              'Status',
  gstRegistrationType: 'GST Registration Type',
  gstin:               'GSTIN',
  pan:                 'PAN',
  gstLegalName:        'Legal Name',
  gstTradeName:        'Trade Name',
  gstStateCode:        'GST State Code',
  mobile:              'Mobile',
  email:               'Email',
  pincode:             'Pincode',
  bankIfscCode:        'Bank IFSC',
  udyamNumber:         'Udyam Number',
  openingBalance:      'Opening Balance',
  openingBalanceType:  'Opening Balance Type',
  creditDays:          'Credit Days'
};

function joinErrorList(errorFields) {
  return errorFields
    .map((f) => {
      const path = Array.isArray(f.name) ? f.name.join('.') : String(f.name);
      const label = FIELD_LABELS[path] || path;
      const msgs = (f.errors || []).join('; ');
      return `• ${label}: ${msgs}`;
    })
    .join('\n');
}

function defaultsFor(mode, record) {
  if (mode !== 'add' && record) {
    return {
      ...record,
      supplierType: record.supplierType || 'business',
      gstRegistrationType: record.gstRegistrationType || 'unregistered',
      country: record.country || 'India',
      // Companion arrays preserved as-is.
      addresses: Array.isArray(record.addresses) ? record.addresses : [],
      contacts:  Array.isArray(record.contacts)  ? record.contacts  : []
    };
  }
  return {
    supplierType: 'business',
    status: 'active',
    isActive: true,
    gstRegistrationType: 'unregistered',
    country: 'India',
    creditDays: 0,
    openingBalance: 0
  };
}

// Section wrapper used inside tabs / steps. Mirrors the Customer
// popup's panel-subtitle style: uppercase, slate-500, slim divider.
function Section({ title, children }) {
  return (
    <div className="erp-supplier-popup__section">
      {title ? <div className="erp-supplier-popup__section-title">{title}</div> : null}
      {children}
    </div>
  );
}

// ── Step / Tab content components ────────────────────────────────
function KycSection({ form, mode, busyGstin, onGstinSearch, groupOptions }) {
  const view = mode === 'view';
  const supplierGroupOptions = useMemo(() => (
    (groupOptions || []).map((g) => ({
      value: g.id,
      // Two-line option render — code as muted prefix, name on top.
      label: (
        <span>
          {g.groupName}
          <code style={{ marginInlineStart: 6, fontSize: 11, color: '#94a3b8' }}>
            {g.groupCode}
          </code>
        </span>
      ),
      __search: `${g.groupName} ${g.groupCode}`.toLowerCase()
    }))
  ), [groupOptions]);
  return (
    <>
      <Section title="GST / Tax">
        <Row gutter={8}>
          <Col xs={24} md={8}>
            <Form.Item label="GST Registration Type" name="gstRegistrationType">
              <Select options={GST_REGISTRATION_TYPE_OPTIONS} disabled={view} />
            </Form.Item>
          </Col>
          <Col xs={24} md={10}>
            <Form.Item label="GSTIN" name="gstin"
              rules={[{
                validator: (_, v) => {
                  if (!v) return Promise.resolve();
                  return GSTIN_RE.test(String(v).toUpperCase())
                    ? Promise.resolve()
                    : Promise.reject(new Error('Invalid GSTIN format'));
                }
              }]}
            >
              <Input maxLength={15} disabled={view}
                onChange={(e) => form.setFieldValue('gstin', String(e.target.value || '').toUpperCase())}
                addonAfter={view ? null : (
                  <Button size="small" type="text" icon={<SearchOutlined />} loading={busyGstin}
                    onClick={onGstinSearch}>Search</Button>
                )}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item label="PAN" name="pan"
              rules={[{
                validator: (_, v) => {
                  if (!v) return Promise.resolve();
                  return PAN_RE.test(String(v).toUpperCase())
                    ? Promise.resolve()
                    : Promise.reject(new Error('Invalid PAN format'));
                }
              }]}
            >
              <Input maxLength={10} disabled={view}
                onChange={(e) => form.setFieldValue('pan', String(e.target.value || '').toUpperCase())} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={8}>
          <Col xs={24} md={12}>
            <Form.Item label="Legal Name" name="gstLegalName">
              <Input disabled={view} maxLength={200} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Trade Name" name="gstTradeName">
              <Input disabled={view} maxLength={200} />
            </Form.Item>
          </Col>
        </Row>
      </Section>
      <Section title="Identity">
        <Row gutter={8}>
          <Col xs={24} md={14}>
            <Form.Item label="Supplier Name" name="supplierName"
              rules={[{ required: true, message: 'Supplier name is required' },
                      { min: 2, max: 200 }]}>
              <Input disabled={view} maxLength={200} />
            </Form.Item>
          </Col>
          <Col xs={24} md={10}>
            <Form.Item label="Display Name" name="displayName">
              <Input disabled={view} maxLength={200} />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item label="Supplier Type" name="supplierType">
              <Select options={SUPPLIER_TYPE_OPTIONS} disabled={view} />
            </Form.Item>
          </Col>
          <Col xs={24} md={10}>
            <Form.Item label="Supplier Group" name="supplierGroupId"
              tooltip="Structured category for the supplier (Raw Glass / Hardware / Transport / …). Managed via the “Supplier Groups” button on the master grid toolbar.">
              <Select
                allowClear
                showSearch
                disabled={view}
                placeholder="Select a supplier group…"
                options={supplierGroupOptions}
                filterOption={(input, option) => {
                  const q = String(input || '').toLowerCase().trim();
                  if (!q) return true;
                  return (option && option.__search ? option.__search : '').includes(q);
                }}
                onChange={(val) => {
                  // Auto-fill defaults from the selected group only when
                  // the corresponding supplier field is still empty/0.
                  const picked = (groupOptions || []).find((g) => g.id === val);
                  if (!picked) return;
                  const curCd = form.getFieldValue('creditDays');
                  if (!curCd || Number(curCd) === 0) {
                    form.setFieldValue('creditDays', picked.defaultCreditDays || 0);
                  }
                  const curPt = form.getFieldValue('paymentTerms');
                  if (!curPt && picked.defaultPaymentTerms) {
                    form.setFieldValue('paymentTerms', picked.defaultPaymentTerms);
                  }
                }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="Status" name="status">
              <Select options={SUPPLIER_STATUS_OPTIONS} disabled={view} />
            </Form.Item>
          </Col>
          <Col xs={24} md={10}>
            <Form.Item label="Category (legacy)" name="supplierCategory"
              tooltip="Free-text category retained for legacy display. New records should pick a Supplier Group instead.">
              <Input disabled={view} maxLength={60} placeholder="e.g. Raw glass, Logistics, Hardware" />
            </Form.Item>
          </Col>
        </Row>
      </Section>
      <Section title="Address">
        <Row gutter={8}>
          <Col xs={24} md={12}>
            <Form.Item label="Address Line 1" name="addressLine1">
              <Input disabled={view} maxLength={250} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Address Line 2" name="addressLine2">
              <Input disabled={view} maxLength={250} />
            </Form.Item>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item label="City" name="city">
              <Input disabled={view} maxLength={100} />
            </Form.Item>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item label="District" name="district">
              <Input disabled={view} maxLength={100} />
            </Form.Item>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item label="State" name="state">
              <Input disabled={view} maxLength={100} />
            </Form.Item>
          </Col>
          <Col xs={6} md={3}>
            <Form.Item label="State Code" name="stateCode">
              <Input disabled={view} maxLength={2} />
            </Form.Item>
          </Col>
          <Col xs={6} md={3}>
            <Form.Item label="Pincode" name="pincode"
              rules={[{
                validator: (_, v) => !v || PINCODE_RE.test(v)
                  ? Promise.resolve()
                  : Promise.reject(new Error('Pincode must be 6 digits'))
              }]}
            >
              <Input disabled={view} maxLength={6} />
            </Form.Item>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item label="Country" name="country">
              <Input disabled={view} maxLength={80} />
            </Form.Item>
          </Col>
        </Row>
      </Section>
      <Section title="Contact">
        <Row gutter={8}>
          <Col xs={24} md={10}>
            <Form.Item label="Contact Person" name="contactPerson">
              <Input disabled={view} maxLength={150} />
            </Form.Item>
          </Col>
          <Col xs={12} md={5}>
            <Form.Item label="Primary Mobile" name="mobile"
              rules={[{
                validator: (_, v) => !v || MOBILE_RE.test(v)
                  ? Promise.resolve()
                  : Promise.reject(new Error('10-digit Indian mobile starting 6/7/8/9'))
              }]}
            >
              <Input disabled={view} maxLength={10} />
            </Form.Item>
          </Col>
          <Col xs={12} md={5}>
            <Form.Item label="Alt Mobile" name="alternateMobile"
              rules={[{
                validator: (_, v) => !v || MOBILE_RE.test(v)
                  ? Promise.resolve()
                  : Promise.reject(new Error('10-digit Indian mobile starting 6/7/8/9'))
              }]}
            >
              <Input disabled={view} maxLength={10} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Email" name="email"
              rules={[{ type: 'email', message: 'Invalid email' }]}>
              <Input disabled={view} maxLength={150} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Phone" name="phone">
              <Input disabled={view} maxLength={30} />
            </Form.Item>
          </Col>
        </Row>
      </Section>
    </>
  );
}

function GstAccountingSection({ mode, regType, msme }) {
  const view = mode === 'view';
  return (
    <>
      <Section title="GST flags">
        <Row gutter={8}>
          <Col xs={12} md={6}>
            <Form.Item label="Reverse Charge" name="isReverseChargeApplicable" valuePropName="checked">
              <Switch disabled={view} />
            </Form.Item>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item label="Composition Dealer" name="isCompositionDealer" valuePropName="checked">
              <Switch disabled={view} />
            </Form.Item>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item label="TDS Applicable" name="tdsApplicable" valuePropName="checked">
              <Switch disabled={view} />
            </Form.Item>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item label="TDS Section" name="tdsSection">
              <Input disabled={view} maxLength={20} placeholder="e.g. 194C" />
            </Form.Item>
          </Col>
        </Row>
      </Section>
      <Section title="MSME / Udyam">
        <Row gutter={8}>
          <Col xs={12} md={6}>
            <Form.Item label="MSME Registered" name="msmeRegistered" valuePropName="checked">
              <Switch disabled={view} />
            </Form.Item>
          </Col>
          <Col xs={24} md={18}>
            <Form.Item label="Udyam Number" name="udyamNumber"
              rules={[
                { required: msme === true, message: 'Required when MSME is enabled' },
                {
                  validator: (_, v) => !v || UDYAM_RE.test(String(v).toUpperCase())
                    ? Promise.resolve()
                    : Promise.reject(new Error('Format: UDYAM-XX-00-0000000'))
                }
              ]}
            >
              <Input disabled={view || msme !== true}
                maxLength={30} placeholder="UDYAM-MH-12-0123456" />
            </Form.Item>
          </Col>
        </Row>
      </Section>
      <Section title="Credit / Opening Balance">
        <Row gutter={8}>
          <Col xs={12} md={4}>
            <Form.Item label="Credit Days" name="creditDays">
              <InputNumber min={0} max={3650} style={{ width: '100%' }} disabled={view} />
            </Form.Item>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item label="Opening Balance" name="openingBalance">
              <InputNumber min={0} step={1} style={{ width: '100%' }} disabled={view}
                formatter={(n) => Number(n || 0).toLocaleString('en-IN')} />
            </Form.Item>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item label="Balance Type" name="openingBalanceType">
              <Select disabled={view} allowClear options={[
                { value: 'debit',  label: 'Debit (we owe)' },
                { value: 'credit', label: 'Credit (they owe us)' }
              ]} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="Payment Terms" name="paymentTerms">
              <Input.TextArea disabled={view} rows={1} maxLength={2000}
                placeholder="e.g. 30 days net, 2% discount if paid in 10 days" />
            </Form.Item>
          </Col>
        </Row>
      </Section>
      {REGISTRATION_TYPES_REQUIRING_GSTIN.has(regType) ? (
        <Alert type="info" showIcon
          message="GSTIN is required for this registration type."
          description='Switch to "unregistered" if the supplier has no GSTIN, or fill the GSTIN field in the KYC tab.' />
      ) : null}
    </>
  );
}

function BankSection({ mode }) {
  const view = mode === 'view';
  return (
    <Row gutter={8}>
      <Col xs={24} md={12}>
        <Form.Item label="Bank Name" name="bankName">
          <Input disabled={view} maxLength={150} />
        </Form.Item>
      </Col>
      <Col xs={24} md={12}>
        <Form.Item label="Branch" name="bankBranch">
          <Input disabled={view} maxLength={150} />
        </Form.Item>
      </Col>
      <Col xs={24} md={12}>
        <Form.Item label="Account Number" name="bankAccountNumber">
          <Input disabled={view} maxLength={40} />
        </Form.Item>
      </Col>
      <Col xs={24} md={12}>
        <Form.Item label="IFSC" name="bankIfscCode"
          rules={[{
            validator: (_, v) => !v || IFSC_RE.test(String(v).toUpperCase())
              ? Promise.resolve()
              : Promise.reject(new Error('Format: 11 chars e.g. SBIN0001234'))
          }]}
        >
          <Input disabled={view} maxLength={11} placeholder="SBIN0001234" />
        </Form.Item>
      </Col>
    </Row>
  );
}

function LinkedLedgerSection({ record }) {
  const linked = record && record.linkedLedger;
  if (!linked) {
    return (
      <Alert type="warning" showIcon
        message="No supplier ledger linked yet."
        description="A ledger account is auto-created under Sundry Creditors on first save." />
    );
  }
  return (
    <div>
      <Alert type="info" showIcon style={{ marginBottom: 12 }}
        message="This supplier is linked to the following ledger account. Posting / re-balancing happens through vouchers (a later stage)." />
      <Row gutter={8}>
        <Col xs={24} md={6}>
          <Form.Item label="Account Code">
            <Input value={linked.account_code} disabled />
          </Form.Item>
        </Col>
        <Col xs={24} md={10}>
          <Form.Item label="Account Name">
            <Input value={linked.account_name} disabled />
          </Form.Item>
        </Col>
        <Col xs={12} md={4}>
          <Form.Item label="Group">
            <Input value={linked.account_group} disabled />
          </Form.Item>
        </Col>
        <Col xs={6} md={2}>
          <Form.Item label="Normal">
            <Input value={linked.normal_balance} disabled />
          </Form.Item>
        </Col>
        <Col xs={6} md={2}>
          <Form.Item label="Active">
            <Tag color={linked.is_active ? 'success' : 'default'}>{linked.is_active ? 'YES' : 'NO'}</Tag>
          </Form.Item>
        </Col>
      </Row>
    </div>
  );
}

function NotesSection({ mode }) {
  const view = mode === 'view';
  return (
    <Form.Item label="Remarks / Notes" name="remarks">
      <Input.TextArea disabled={view} rows={6} maxLength={2000}
        placeholder="Internal notes — payment behavior, dispute history, delivery SLA, etc." />
    </Form.Item>
  );
}

// ─────────────────────────────────────────────────────────────────
// Top-level modal
// ─────────────────────────────────────────────────────────────────
export default function SupplierModal({ open, mode, record, onClose, onSaved, runWithApproval }) {
  const [form] = Form.useForm();
  const [activeStep, setActiveStep] = useState(0);
  const [activeTab, setActiveTab]   = useState('kyc');
  const [busy, setBusy]             = useState(false);
  const [busyGstin, setBusyGstin]   = useState(false);

  // Re-watch reactive fields for conditional UI without remounting.
  const regType = Form.useWatch('gstRegistrationType', form);
  const msme    = Form.useWatch('msmeRegistered',      form);

  // Stage 6B.3B — Supplier Group lookup. Fetched once per modal open.
  // Active groups only; the dropdown is searchable client-side.
  const [groupOptions, setGroupOptions] = useState([]);
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    supplierGroupApi.lookup('', 500)
      .then((rows) => { if (!cancelled) setGroupOptions(Array.isArray(rows) ? rows : []); })
      .catch(() => { if (!cancelled) setGroupOptions([]); });
    return () => { cancelled = true; };
  }, [open]);

  // Hydrate form when (open / mode / record) changes.
  useEffect(() => {
    if (!open) return;
    form.resetFields();
    form.setFieldsValue(defaultsFor(mode, record));
    setActiveStep(0);
    setActiveTab('kyc');
  }, [open, mode, record, form]);

  const handleGstinSearch = useCallback(async () => {
    const gstin = (form.getFieldValue('gstin') || '').trim().toUpperCase();
    if (!gstin && gstin !== 'URP') {
      message.info('Type a GSTIN (or "URP") before searching.');
      return;
    }
    setBusyGstin(true);
    try {
      const data = await supplierApi.gstinSearch(gstin);
      if (data && data.urp) {
        form.setFieldsValue({ gstRegistrationType: 'unregistered', gstin: null });
        message.success('Marked as unregistered (URP).');
        return;
      }
      const c = (data && data.customer) || {};
      const next = {};
      if (c.gstin)        next.gstin = c.gstin;
      if (c.pan)          next.pan = c.pan;
      if (c.gstLegalName) next.gstLegalName = c.gstLegalName;
      if (c.gstTradeName) next.gstTradeName = c.gstTradeName;
      if (c.customerName && !form.getFieldValue('supplierName')) {
        next.supplierName = c.customerName;
      }
      if (c.gstStateCode) next.gstStateCode = c.gstStateCode;
      if (c.address) {
        const a = c.address;
        if (a.line1   && !form.getFieldValue('addressLine1')) next.addressLine1 = a.line1;
        if (a.line2   && !form.getFieldValue('addressLine2')) next.addressLine2 = a.line2;
        if (a.city    && !form.getFieldValue('city'))         next.city    = a.city;
        if (a.state   && !form.getFieldValue('state'))        next.state   = a.state;
        if (a.pincode && !form.getFieldValue('pincode'))      next.pincode = a.pincode;
      }
      next.gstRegistrationType = 'registered_regular';
      form.setFieldsValue(next);
      if (data && data.warning === 'gsp_provider_not_configured') {
        message.warning('GSP provider not configured — using local GSTIN parsing only.');
      } else {
        message.success('GSTIN search complete.');
      }
    } catch (e) {
      message.error(e?.response?.data?.error?.message || 'GSTIN search failed.');
    } finally {
      setBusyGstin(false);
    }
  }, [form]);

  const submit = useCallback(async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (e) {
      if (e && Array.isArray(e.errorFields)) {
        Modal.warning({
          title: 'Please fix these fields',
          content: (
            <pre style={{ whiteSpace: 'pre-wrap', fontSize: 13, fontFamily: 'inherit' }}>
              {joinErrorList(e.errorFields)}
            </pre>
          ),
          okText: 'Close'
        });
        // Switch to the first tab/step that contains an error.
        const firstField = e.errorFields[0]?.name;
        if (firstField) {
          const p = Array.isArray(firstField) ? firstField[0] : firstField;
          if (mode === 'add') {
            if (['gstRegistrationType','gstin','pan','gstLegalName','gstTradeName',
                 'supplierName','displayName','supplierType','supplierCategory','supplierGroupId','status',
                 'addressLine1','addressLine2','city','district','state','stateCode','pincode',
                 'country','contactPerson','mobile','alternateMobile','email','phone'].includes(p)) {
              setActiveStep(0);
            } else if (['msmeRegistered','udyamNumber','tdsApplicable','tdsSection',
                        'isReverseChargeApplicable','isCompositionDealer',
                        'creditDays','openingBalance','openingBalanceType','paymentTerms'].includes(p)) {
              setActiveStep(1);
            } else {
              setActiveStep(2);
            }
          } else {
            if (['bankName','bankAccountNumber','bankIfscCode','bankBranch'].includes(p)) {
              setActiveTab('bank');
            } else if (['msmeRegistered','udyamNumber','creditDays','openingBalance',
                        'openingBalanceType','paymentTerms','tdsApplicable','tdsSection',
                        'isReverseChargeApplicable','isCompositionDealer'].includes(p)) {
              setActiveTab('gst');
            } else {
              setActiveTab('kyc');
            }
          }
        }
      } else {
        message.error('Please fix the highlighted fields.');
      }
      return;
    }

    setBusy(true);
    try {
      const payload = { ...values };
      if (payload.gstin)        payload.gstin        = String(payload.gstin).toUpperCase();
      if (payload.pan)          payload.pan          = String(payload.pan).toUpperCase();
      if (payload.bankIfscCode) payload.bankIfscCode = String(payload.bankIfscCode).toUpperCase();
      if (payload.udyamNumber)  payload.udyamNumber  = String(payload.udyamNumber).toUpperCase();

      let saved;
      if (mode === 'add') {
        saved = await supplierApi.create(payload);
        message.success(`Supplier ${saved.supplierCode} created.`);
      } else {
        saved = await supplierApi.update(record.id, payload);
        message.success('Supplier updated.');
      }
      onSaved && onSaved(saved);
    } catch (e) {
      const code = e?.response?.data?.error?.code;
      const msg  = e?.response?.data?.error?.message || e.message || 'Save failed.';
      if (code === 'SUPPLIER_GSTIN_EXISTS') {
        Modal.error({ title: 'Duplicate GSTIN', content: msg });
      } else {
        message.error(msg, 6);
      }
    } finally {
      setBusy(false);
    }
  }, [form, mode, record, onSaved]);

  // ── Render ────────────────────────────────────────────────────
  const title = useMemo(() => {
    if (mode === 'add')  return 'Add Supplier';
    if (mode === 'view') return `View Supplier${record?.supplierCode ? ' — ' + record.supplierCode : ''}`;
    return `Edit Supplier${record?.supplierCode ? ' — ' + record.supplierCode : ''}`;
  }, [mode, record]);

  const renderStep = (idx) => {
    if (idx === 0) return <KycSection form={form} mode={mode} busyGstin={busyGstin}
                            onGstinSearch={handleGstinSearch} groupOptions={groupOptions} />;
    if (idx === 1) return <GstAccountingSection mode={mode} regType={regType} msme={msme} />;
    return <BankSection mode={mode} />;
  };

  const stepOrder = useMemo(() => ([
    { key: 'kyc',     label: 'KYC' },
    { key: 'gst',     label: 'GST & Accounting' },
    { key: 'bank',    label: 'Bank & Defaults' }
  ]), []);

  const tabBody = (node) => <div style={{ padding: '12px 16px' }}>{node}</div>;
  const tabItems = useMemo(() => ([
    { key: 'kyc',     label: 'KYC',
      children: tabBody(<KycSection form={form} mode={mode} busyGstin={busyGstin}
                          onGstinSearch={handleGstinSearch} groupOptions={groupOptions} />) },
    { key: 'gst',     label: 'GST & Accounting',
      children: tabBody(<GstAccountingSection mode={mode} regType={regType} msme={msme} />) },
    { key: 'bank',    label: 'Bank Details',
      children: tabBody(<BankSection mode={mode} />) },
    { key: 'ledger',  label: 'Linked Ledger',
      children: tabBody(<LinkedLedgerSection record={record} />) },
    { key: 'notes',   label: 'Notes / Defaults',
      children: tabBody(<NotesSection mode={mode} />) }
  ]), [form, mode, busyGstin, handleGstinSearch, regType, msme, record, groupOptions]);

  const footer = mode === 'view' ? (
    <Space><Button onClick={onClose}>Close</Button></Space>
  ) : mode === 'add' ? (
    <Space>
      <Button disabled={activeStep === 0} onClick={() => setActiveStep((s) => s - 1)}>
        Back
      </Button>
      {activeStep < stepOrder.length - 1 ? (
        <Button type="primary" onClick={() => setActiveStep((s) => s + 1)}>Next</Button>
      ) : (
        <Button type="primary" loading={busy} onClick={submit}>Create Supplier</Button>
      )}
      <Button onClick={onClose}>Cancel</Button>
    </Space>
  ) : (
    <Space>
      <Button type="primary" loading={busy} onClick={submit}>Save Changes</Button>
      <Button onClick={onClose}>Cancel</Button>
    </Space>
  );

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onClose}
      footer={footer}
      width={1024}
      centered
      destroyOnClose
      maskClosable={false}
      className="erp-supplier-modal"
      styles={{ body: { maxHeight: '72vh', overflowY: 'auto', padding: 0 } }}
    >
      <Form
        form={form}
        layout="vertical"
        preserve
        className={`erp-supplier-drawer__form${mode === 'view' ? ' is-view-mode' : ''}`}
      >
        {mode === 'add' ? (
          <>
            {/* Sticky compact wizard nav — mirrors Customer popup. */}
            <div className="erp-supplier-wizard__nav">
              <Steps current={activeStep} items={stepOrder} size="small" responsive={false} />
            </div>
            {/* Render all steps mounted but hide non-active to keep Form.Items
                registered so validateFields covers every step. */}
            <div style={{ padding: '12px 16px' }}>
              {[0, 1, 2].map((i) => (
                <div key={i} style={{ display: i === activeStep ? 'block' : 'none' }}>
                  {renderStep(i)}
                </div>
              ))}
            </div>
          </>
        ) : (
          <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems}
            tabBarStyle={{ marginBottom: 0 }} />
        )}
      </Form>
    </Modal>
  );
}
